import { CylinderGeometry, Mesh, Object3D } from 'three';
import { Point3D } from '../../../team-apis/otterdam/answers/shape-typings';
import { H3BasicMaterial } from '../../materials/basic/h3-basic-material';
import { REFERENCE_AXIS } from '../../utils/point-cloud-utils';
import { directionBetween, distanceBetween } from '../../utils/point-utils';

export interface ArrowOptions {
  radius: number;
  direction?: Point3D;
  headLength?: number;
  headWidth?: number;
}

export class ArrowMesh extends Object3D {
  override readonly type = 'ArrowMesh';

  private body: Mesh<CylinderGeometry, H3BasicMaterial>;
  private head: Mesh<CylinderGeometry, H3BasicMaterial>;

  constructor(private options: ArrowOptions) {
    super();

    const unitScale = 1;
    const halfUnit = unitScale / 2;
    this.body = new Mesh(new CylinderGeometry(halfUnit, halfUnit, unitScale));
    this.head = new Mesh(new CylinderGeometry(0, halfUnit, unitScale));
    this.head.userData = { meshType: 'ArrowHead' };
    this.add(this.head, this.body);

    const direction = this.options.direction || [...REFERENCE_AXIS];
    this.setDirection(direction);
    this.setLength(unitScale);
  }

  dispose() {
    this.head.geometry.dispose();
    this.body.geometry.dispose();
  }

  setMaterial(material: H3BasicMaterial) {
    this.body.material = material;
    this.head.material = material;
  }

  placeAt(origin: Point3D, destination: Point3D) {
    const backToFront = directionBetween(origin, destination);
    this.setDirection(backToFront);

    const length = distanceBetween(origin, destination);
    this.setLength(length);

    this.position.fromArray(origin);
  }

  private setLength(fullLength: number) {
    const {
      radius,
      headLength = fullLength * 0.4,
      headWidth = radius * 15,
    } = this.options;

    const bodyLength = Math.max(0.01, fullLength - headLength);
    this.body.scale.set(radius, bodyLength, radius);
    this.body.position.y = bodyLength / 2;

    this.head.scale.set(headWidth, headLength, headWidth);
    this.head.position.y = bodyLength + headLength / 2;
  }

  private setDirection(direction: Point3D) {
    this.quaternion.setFromUnitVectors(this.up, {
      x: direction[0],
      y: direction[1],
      z: direction[2],
    });
  }
}
